/**
 * HistoryRail — right-column rail rendered next to the elevation panels.
 * Shows the rolling 5-deep undo timeline at the top and the named pinned
 * checkpoints at the bottom. Pure presentational — receives all state +
 * callbacks from the ElevationViews orchestrator.
 *
 * Layout decision: this lives in its own component (extracted from
 * `ElevationViews.jsx` in iter 37) so the orchestrator stays under 500
 * lines and the rail can be re-skinned / re-positioned without touching
 * the orchestrator's state machine.
 */
import HistoryThumbnail from "./HistoryThumbnail";

const fmtAgo = (ts) => {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 5)   return "just now";
  if (s < 60)  return `${s}s ago`;
  if (s < 600) return `${Math.round(s / 60)}m ago`;
  return new Date(ts).toLocaleTimeString();
};

export default function HistoryRail({
  L, W, UNDO_LIMIT,
  undoStack, jumpToHistory,
  pinnedCheckpoints, restorePinned, removePinned,
  onClose,
}) {
  return (
    <aside className="w-64 shrink-0 border border-[#00E5FF]/25 bg-[#050608] p-2 space-y-2 max-h-[640px] overflow-y-auto"
           data-testid="elevation-history-rail">
      <div className="label-tag !text-[9px] flex items-center justify-between">
        <span>// history · {undoStack.length}/{UNDO_LIMIT}</span>
        <button type="button"
                onClick={onClose}
                className="text-gray-500 hover:text-white text-[10px]"
                title="Hide rail">
          ×
        </button>
      </div>
      {undoStack.length === 0 ? (
        <div className="text-[10px] text-gray-500 font-mono py-2 text-center">
          No history yet — drag any equipment to start tracking.
        </div>
      ) : (
        undoStack.slice().reverse().map((entry, revIdx) => {
          const idx = undoStack.length - 1 - revIdx;
          return (
            <button type="button" key={`h-${entry.ts}-${idx}`}
                    onClick={() => jumpToHistory(idx)}
                    className="w-full flex items-center gap-2 p-1 border border-white/10 hover:border-[#FFCC00] hover:bg-white/5 text-left"
                    data-testid={`elevation-history-entry-${idx}`}>
              <HistoryThumbnail snap={entry.snap} L={L} W={W} />
              <div className="flex-1 min-w-0 font-mono text-[10px]">
                <div className="text-[#00E5FF] truncate">
                  #{idx + 1} · {fmtAgo(entry.ts)}
                </div>
                <div className="text-gray-400">
                  {entry.summary.items} items · {entry.summary.trays} trays
                </div>
              </div>
            </button>
          );
        })
      )}
      {pinnedCheckpoints.length > 0 && (
        <>
          <div className="label-tag !text-[9px] pt-2 border-t border-white/10">
            // pinned ({pinnedCheckpoints.length})
          </div>
          {pinnedCheckpoints.map((entry, idx) => (
            <div key={`pin-${entry.ts}-${idx}`}
                 className="flex items-center gap-2 p-1 border border-[#FFCC00]/30 hover:border-[#FFCC00] hover:bg-[#FFCC00]/5"
                 data-testid={`elevation-pinned-entry-${idx}`}>
              <button type="button"
                      onClick={() => restorePinned(idx)}
                      className="flex items-center gap-2 flex-1 min-w-0 text-left">
                <HistoryThumbnail snap={entry.snap} L={L} W={W} />
                <div className="flex-1 min-w-0 font-mono text-[10px]">
                  <div className="text-[#FFCC00] truncate font-bold">
                    📌 {entry.name}
                  </div>
                  <div className="text-gray-400">
                    {entry.summary.items} items · {entry.summary.trays} trays
                  </div>
                </div>
              </button>
              <button type="button"
                      onClick={() => removePinned(idx)}
                      className="text-gray-500 hover:text-red-400 text-xs px-1"
                      title={`Remove pinned "${entry.name}"`}>
                ×
              </button>
            </div>
          ))}
        </>
      )}
    </aside>
  );
}
